import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  WritableSignal,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-email-mfa',
  templateUrl: './email-mfa.component.html',
  styleUrls: ['./email-mfa.component.scss'],
  standalone: true,
  imports: [NgClass, ButtonModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailMfaComponent implements OnChanges, OnDestroy {
  @Input() email = '';
  @Input() isLoading = false;
  @Input() hasError = false;
  @Output() verify = new EventEmitter<string>();
  @Output() resend = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  readonly resendDelaySeconds = 30;
  readonly codeIndexes = [0, 1, 2, 3, 4, 5];
  codeDigits = ['', '', '', '', '', ''];
  codeValue: WritableSignal<string> = signal('');
  resendSecondsLeft: WritableSignal<number> = signal(this.resendDelaySeconds);

  @ViewChildren('codeInput') private codeInputs?: QueryList<ElementRef<HTMLInputElement>>;

  private resendTimer?: ReturnType<typeof setInterval>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['email'] && this.email) {
      this.restartResendTimer();
    }
  }

  submit(): void {
    const code = this.getCode();

    if (code.length !== 6) {
      return;
    }

    this.verify.emit(code);
  }

  onDigitInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');

    if (!digits) {
      this.codeDigits[index] = '';
      input.value = '';
      this.updateCodeValue();
      return;
    }

    this.applyDigits(digits, digits.length >= this.codeDigits.length ? 0 : index);
  }

  onDigitKeydown(event: KeyboardEvent, index: number): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (event.key.length === 1 && !/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Backspace' && !this.codeDigits[index] && index > 0) {
      event.preventDefault();
      this.codeDigits[index - 1] = '';
      this.updateCodeValue();
      this.focusDigit(index - 1);
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusDigit(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < this.codeDigits.length - 1) {
      event.preventDefault();
      this.focusDigit(index + 1);
    }
  }

  onDigitPaste(event: ClipboardEvent, index: number): void {
    const pastedDigits = event.clipboardData?.getData('text').replace(/\D/g, '') ?? '';

    if (!pastedDigits) {
      return;
    }

    event.preventDefault();
    this.applyDigits(
      pastedDigits,
      pastedDigits.length >= this.codeDigits.length ? 0 : index
    );
  }

  resendCode(): void {
    if (this.resendSecondsLeft() > 0 || this.isLoading) {
      return;
    }

    this.clearCode();
    this.restartResendTimer();
    this.resend.emit();
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
  }

  private restartResendTimer(): void {
    this.clearResendTimer();
    this.resendSecondsLeft.set(this.resendDelaySeconds);

    this.resendTimer = setInterval(() => {
      const nextValue = this.resendSecondsLeft() - 1;
      this.resendSecondsLeft.set(Math.max(nextValue, 0));

      if (nextValue <= 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = undefined;
    }
  }

  private applyDigits(digits: string, startIndex: number): void {
    const digitList = digits.slice(0, this.codeDigits.length - startIndex).split('');

    digitList.forEach((digit, offset) => {
      this.codeDigits[startIndex + offset] = digit;
    });

    this.updateCodeValue();
    this.syncInputValues();
    this.focusDigit(Math.min(startIndex + digitList.length, this.codeDigits.length - 1));
  }

  private clearCode(): void {
    this.codeDigits = ['', '', '', '', '', ''];
    this.updateCodeValue();
    this.syncInputValues();
    this.focusDigit(0);
  }

  private focusDigit(index: number): void {
    queueMicrotask(() => {
      const input = this.codeInputs?.get(index)?.nativeElement;
      input?.focus();
      input?.select();
    });
  }

  private syncInputValues(): void {
    this.codeInputs?.forEach((inputRef, index) => {
      inputRef.nativeElement.value = this.codeDigits[index];
    });
  }

  private getCode(): string {
    return this.codeDigits.join('');
  }

  private updateCodeValue(): void {
    this.codeValue.set(this.getCode());
  }
}
